/**
 * Video Mutation Hooks
 *
 * TanStack Query mutation hooks for video actions
 * with optimistic updates and cache invalidation
 */

import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { videoClient } from '../client/video.client';
import { queryKeys } from '../query-keys';
import type { MeResponse, Video, PaginatedResponse } from '../types';

/**
 * Hook to toggle like on a video with optimistic update
 * Updates both video detail cache and series play cache
 */
export function useToggleLike() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (videoId: string | number) => videoClient.toggleLike(videoId),
    onMutate: async (videoId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.videos.detail(videoId) });
      await queryClient.cancelQueries({
        queryKey: [...queryKeys.series.detail(videoId), 'play'],
      });

      const previousVideo = queryClient.getQueryData<Video>(queryKeys.videos.detail(videoId));
      const previousSeriesPlay = queryClient.getQueryData<{ video: Video }>([
        ...queryKeys.series.detail(videoId),
        'play',
      ]);

      const currentHasLiked = previousVideo?.has_liked ?? previousSeriesPlay?.video?.has_liked;
      const newLikedState = !currentHasLiked;
      const currentLikesCount =
        previousVideo?.likes_count ?? previousSeriesPlay?.video?.likes_count ?? 0;
      const newLikesCount = newLikedState ? currentLikesCount + 1 : currentLikesCount - 1;

      if (previousVideo) {
        queryClient.setQueryData<Video>(queryKeys.videos.detail(videoId), {
          ...previousVideo,
          has_liked: newLikedState,
          likes_count: newLikesCount,
        });
      }

      if (previousSeriesPlay?.video) {
        queryClient.setQueryData<{ video: Video }>([...queryKeys.series.detail(videoId), 'play'], {
          ...previousSeriesPlay,
          video: {
            ...previousSeriesPlay.video,
            has_liked: newLikedState,
            likes_count: newLikesCount,
          },
        });
      }

      return { previousVideo, previousSeriesPlay };
    },
    onError: (_err, videoId, context) => {
      if (context?.previousVideo) {
        queryClient.setQueryData(queryKeys.videos.detail(videoId), context.previousVideo);
      }
      if (context?.previousSeriesPlay) {
        queryClient.setQueryData(
          [...queryKeys.series.detail(videoId), 'play'],
          context.previousSeriesPlay
        );
      }
    },
    onSettled: (_data, _error, videoId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.videos.detail(videoId) });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.series.detail(videoId), 'play'],
      });
    },
  });
}

/**
 * Hook to toggle dislike on a video with optimistic update
 */
export function useToggleDislike() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (videoId: string | number) => videoClient.toggleDislike(videoId),
    onMutate: async (videoId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.videos.detail(videoId) });

      const previous = queryClient.getQueryData<Video>(queryKeys.videos.detail(videoId));

      if (previous) {
        const currentDisliked = previous.has_disliked ?? false;
        queryClient.setQueryData<Video>(queryKeys.videos.detail(videoId), {
          ...previous,
          has_disliked: !currentDisliked,
          dislikes_count: currentDisliked
            ? (previous.dislikes_count || 0) - 1
            : (previous.dislikes_count || 0) + 1,
        });
      }

      return { previous };
    },
    onError: (_err, videoId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.videos.detail(videoId), context.previous);
      }
    },
    onSettled: (_data, _error, videoId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.videos.detail(videoId) });
    },
  });
}

/** Update a single video's bookmark flags if it matches the target uuid */
function updateVideoBookmark(v: Video, videoUuid: string, newBookmarked: boolean): Video {
  return v.uuid === videoUuid
    ? { ...v, is_bookmarked: newBookmarked, in_watchlist: newBookmarked }
    : v;
}

/** Apply bookmark update or removal filter to a page's data array */
function updatePageData(
  data: Video[],
  videoUuid: string,
  newBookmarked: boolean,
  filterOnRemove: boolean
): Video[] {
  if (!newBookmarked && filterOnRemove) {
    return data.filter((v) => v.uuid !== videoUuid);
  }
  return data.map((v) => updateVideoBookmark(v, videoUuid, newBookmarked));
}

/** Update pages in an InfiniteData structure, optionally filtering out the video */
function updateInfinitePages(
  data: InfiniteData<PaginatedResponse<Video>>,
  videoUuid: string,
  newBookmarked: boolean,
  filterOnRemove: boolean
): InfiniteData<PaginatedResponse<Video>> {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: Array.isArray(page.data)
        ? updatePageData(page.data, videoUuid, newBookmarked, filterOnRemove)
        : page.data, // safety: skip pages without a data array
    })),
  };
}

/**
 * Hook to toggle bookmark on a video with multi-cache optimistic updates.
 *
 * Updates the video detail cache, all home list caches (infinite),
 * and the bookmarked list cache (flat or infinite) so every component
 * showing the same video reflects the change instantly.
 *
 * Modeled after `useToggleShortLike` in shorts.mutations.ts.
 */
export function useToggleBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      videoUuid,
    }: {
      videoUuid: string;
      videoId?: number | undefined;
      currentlyBookmarked?: boolean;
    }) => videoClient.toggleBookmark(videoUuid),

    onMutate: async ({ videoUuid, currentlyBookmarked }) => {
      const cacheKey = videoUuid;
      const playKey = [...queryKeys.videos.detail(cacheKey), 'play'] as const;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.videos.detail(cacheKey) }),
        queryClient.cancelQueries({ queryKey: playKey }),
        queryClient.cancelQueries({ queryKey: queryKeys.home.all }),
        queryClient.cancelQueries({ queryKey: queryKeys.videos.bookmarked() }),
      ]);

      const previousDetail = queryClient.getQueryData<Video>(queryKeys.videos.detail(cacheKey));
      const previousPlay = queryClient.getQueryData<{ video: Video; videos: Video[] }>(playKey);

      // Read latest overrides so double-toggles use optimistic state, not stale server state
      const previousOverrides =
        queryClient.getQueryData<Record<string, boolean>>(queryKeys.videos.bookmarkOverrides()) ??
        {};

      const wasBookmarked =
        previousOverrides[videoUuid] ??
        previousDetail?.is_bookmarked ??
        previousDetail?.in_watchlist ??
        previousPlay?.video?.is_bookmarked ??
        previousPlay?.video?.in_watchlist ??
        currentlyBookmarked ??
        false;
      const newBookmarked = !wasBookmarked;

      // Write bookmark override so playlist consumers react instantly
      queryClient.setQueryData<Record<string, boolean>>(queryKeys.videos.bookmarkOverrides(), {
        ...previousOverrides,
        [videoUuid]: newBookmarked,
      });

      // Optimistic update for detail cache
      if (previousDetail) {
        queryClient.setQueryData<Video>(queryKeys.videos.detail(cacheKey), {
          ...previousDetail,
          is_bookmarked: newBookmarked,
          in_watchlist: newBookmarked,
        });
      }

      // Optimistic update for play cache (used by useVideoPlay / SaveButton)
      if (previousPlay?.video) {
        queryClient.setQueryData(playKey, {
          ...previousPlay,
          video: {
            ...previousPlay.video,
            is_bookmarked: newBookmarked,
            in_watchlist: newBookmarked,
          },
        });
      }

      // --- Snapshot & update video-typed home list caches ---
      const previousHomeLists = queryClient.getQueriesData<InfiniteData<PaginatedResponse<Video>>>({
        queryKey: queryKeys.home.all,
      });

      previousHomeLists.forEach(([key, data]) => {
        if (data && 'pages' in data) {
          queryClient.setQueryData(key, updateInfinitePages(data, videoUuid, newBookmarked, false));
        }
      });

      // --- Snapshot & update bookmarked list caches ---
      const previousBookmarkedLists = queryClient.getQueriesData<
        PaginatedResponse<Video> | InfiniteData<PaginatedResponse<Video>>
      >({
        queryKey: queryKeys.videos.bookmarked(),
      });

      previousBookmarkedLists.forEach(([key, data]) => {
        if (!data) return;

        if ('pages' in data) {
          queryClient.setQueryData(key, updateInfinitePages(data, videoUuid, newBookmarked, true));
        } else if ('data' in data && Array.isArray(data.data)) {
          queryClient.setQueryData<PaginatedResponse<Video>>(key, {
            ...data,
            data: newBookmarked
              ? data.data.map((v) => updateVideoBookmark(v, videoUuid, newBookmarked))
              : data.data.filter((v) => v.uuid !== videoUuid),
          });
        }
      });

      // Collect all snapshots for rollback
      const previousLists: [readonly unknown[], unknown][] = [
        ...previousHomeLists,
        ...previousBookmarkedLists,
      ];

      return {
        previousDetail,
        previousPlay,
        previousLists,
        previousOverrides,
        cacheKey,
        videoUuid,
        newBookmarked,
      };
    },

    // No onSuccess reconcile: BE now returns the truthful post-toggle state and
    // busts the per-user video-state cache (FRONT-198 fixed server-side), so the
    // optimistic onMutate value + the onSettled refetch converge correctly.

    onError: (_err, _vars, context) => {
      if (context?.previousDetail && context.cacheKey) {
        queryClient.setQueryData(queryKeys.videos.detail(context.cacheKey), context.previousDetail);
      }

      if (context?.previousPlay && context.cacheKey) {
        queryClient.setQueryData(
          [...queryKeys.videos.detail(context.cacheKey), 'play'],
          context.previousPlay
        );
      }

      context?.previousLists.forEach(([key, data]) => {
        if (data) {
          queryClient.setQueryData(key, data);
        }
      });

      // Roll back bookmark overrides to pre-mutation snapshot
      if (context?.previousOverrides) {
        queryClient.setQueryData(queryKeys.videos.bookmarkOverrides(), context.previousOverrides);
      }
    },

    onSettled: (_data, _error, _vars, context) => {
      // Invalidate detail, play, and bookmarked for server truth; skip home lists
      // to avoid unnecessary refetches and scroll position loss
      if (context?.cacheKey) {
        queryClient.invalidateQueries({ queryKey: queryKeys.videos.detail(context.cacheKey) });
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.videos.detail(context.cacheKey), 'play'],
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.videos.bookmarked() });
    },
  });
}

/**
 * Hook to toggle autoplay preference with optimistic update
 */
export function useToggleAutoplay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => videoClient.toggleAutoplay(),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.auth.me() });

      const previousData = queryClient.getQueryData<MeResponse>(queryKeys.auth.me());

      if (previousData?.user) {
        queryClient.setQueryData<MeResponse>(queryKeys.auth.me(), {
          ...previousData,
          user: {
            ...previousData.user,
            video_autoplay: !previousData.user.video_autoplay,
          },
        });
      }

      return { previousData };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.auth.me(), context.previousData);
      }
      toast.error('Failed to update autoplay setting');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
  });
}
